import { createContext, createSignal, onCleanup, type Accessor } from 'solid-js'
import type { RemoteArtworkStatus } from '~/lib/remote-image'

/**
 * The banner load status of every hero card in the DOM, the starter pack's
 * included: on a Featured landing each one is part of the reveal surface
 * (GLOSSARY.md "Reveal surface"). A card tracks its banner for as long as it
 * is mounted, so an omitted or expired hero — no card — has nothing to wait
 * for, and a card that remounts (the starter pack moving to the end of the
 * row once owned) is tracked afresh.
 */
export interface HeroBanners {
  statuses: Accessor<RemoteArtworkStatus[]>
  // Called from a card's owner: the entry leaves with the card.
  track: (status: Accessor<RemoteArtworkStatus>) => void
}

export function createHeroBanners(): HeroBanners {
  const [tracked, setTracked] = createSignal<Accessor<RemoteArtworkStatus>[]>(
    [],
  )
  return {
    statuses: () => tracked().map((status) => status()),
    track: (status) => {
      setTracked((previous) => [...previous, status])
      onCleanup(() =>
        setTracked((previous) => previous.filter((entry) => entry !== status)),
      )
    },
  }
}

// Provided once by the app; a hero row mounted without it (component
// tests) tracks nothing.
export const HeroBannersContext = createContext<HeroBanners>()
